import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Camera, Crown, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useBoscoCaptureAccess } from './hooks/useBoscoCaptureAccess';
import { detectDevice } from './services/deviceDetector';
import BoscoCaptureDesktopHandoff from './BoscoCaptureDesktopHandoff';
import BoscoCaptureCameraView from './BoscoCaptureCameraView';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'sip_token';

/**
 * BoscoCapturePage — Phase 1 entry surface.
 *
 * Decides:
 *   1. Authentication — if no token, sends user to /login.
 *   2. Entitlement — Elite Individual only (all other tiers see the dormant upsell).
 *   3. Device — mobile → camera shell (Phase 2 placeholder); desktop → handoff page.
 *   4. Handoff token — when arriving via the QR link, validates the token then proceeds.
 */
export default function BoscoCapturePage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const handoffToken = search.get('handoff');

  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sip_user') || 'null'); } catch { return null; }
  });
  const { loading, data: access, error } = useBoscoCaptureAccess({ enabled: !!user });
  const device = detectDevice();
  const [handoffValid, setHandoffValid] = useState(null);

  // Validate handoff token (idempotent)
  useEffect(() => {
    if (!handoffToken) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/bosco-capture/handoff/${handoffToken}`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          toast.error(d?.detail?.message || 'Handoff link is invalid or expired.');
          setHandoffValid(false);
          return;
        }
        setHandoffValid(true);
      } catch {
        setHandoffValid(false);
      }
    })();
  }, [handoffToken]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <h2 className="text-lg font-semibold">Please sign in</h2>
          <p className="text-sm text-slate-500 mt-1">BOSCO Capture™ requires a teacher account.</p>
          <Button className="mt-4" onClick={() => navigate('/login')}>Sign in</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="bosco-capture-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error || !access) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <h2 className="text-lg font-semibold">Could not load BOSCO Capture</h2>
          <p className="text-sm text-slate-500 mt-1">{error || 'No access information available.'}</p>
          <Button className="mt-4" variant="outline" onClick={() => navigate('/teacher-assignments')}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to assignments
          </Button>
        </div>
      </div>
    );
  }

  // Dormant — BOSCO Capture is EXCLUSIVE to Elite (iter 90). Every
  // non-Elite user (individual OR school) lands here with the same upsell.
  if (!access.enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="bosco-capture-dormant">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-600 text-white mb-4 shadow-lg shadow-emerald-600/30 relative">
            <Camera className="h-7 w-7" />
            <Crown className="absolute -top-1.5 -right-1.5 h-4 w-4 text-amber-300" />
          </div>
          <h2 className="text-xl font-bold">BOSCO Capture™ is exclusive to Elite</h2>
          <p className="text-sm text-slate-500 mt-2">{access.dormantUpsellBody}</p>
          <div className="flex items-center gap-2 justify-center mt-5">
            <Button variant="outline" onClick={() => navigate('/teacher-assignments')}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                const targetPlan = access.isIndividual ? 'elite' : 'pro';
                // Hard navigation — guarantees TeacherAssignmentsPage
                // re-mounts fresh so its UpgradeDialog useEffect picks
                // up the URL params on the FIRST render (SPA-navigate
                // was flaky on the individual free-tier path).
                window.location.assign(`/teacher-assignments?upgrade=${targetPlan}&reason=bosco-capture`);
              }}
              data-testid="bosco-capture-dormant-upgrade"
            >
              {access.isIndividual ? 'Upgrade to Elite' : 'Upgrade School to Pro'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Entitled. Branch on device.
  if (!device.isMobile) {
    return <BoscoCaptureDesktopHandoff userEmail={user?.email} />;
  }

  // Mobile + Elite → render the camera shell (Phase 2).
  // Token validation (when arriving via handoff link) runs in parallel
  // — failures are surfaced as a toast but do not block the camera.
  return <BoscoCaptureCameraView />;
}
